import { useEffect } from 'react';
import { Crisp } from 'crisp-sdk-web';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isPublicRoute } from '@shared/utils';
import { useauthMeQuery, useGetWalletDetailsV2Query, useListSubscriptionQuery } from '@/types/generated/graphql';
import { COOKIE_KEY } from '@shared/constants';
import Cookies from 'js-cookie';
import useRouteChecker from './use-route-checker';
import { useSupportChatStore } from '@shared/store/support-chat.store';
import { useEffectiveThemeMode } from '@shared/store/theme.store';

interface SupportedUser {
  name: string;
  email: string;
}

type UserPlan = `${string}'-'${string}'-'${string}` | `${string}-${string}`;

export type UseSupportChatReturn = {
  toggleSupportChat: VoidFunction;
  openSupportChat: VoidFunction;
  closeSupportChat: VoidFunction;
  updateUserInChat: (user: SupportedUser) => void;
  trackEvent: (name: string, data?: object) => void;
  setMessage: (message: string) => void;
};

function transformUserPlan(userPlan: UserPlan) {
  // this transforms 'basic-usd-montly' to 'Deal: Basic-Monthly'
  // and 'custom-plan' to 'Deal: Custom-Plan'
  // as requested by sales;
  try {
    let result;
    const planArr = userPlan.split('-');

    if (planArr.length === 2) {
      throw void 0;
    } else {
      const [plan, _, duration] = planArr;
      result = plan + '-' + duration;
    }

    return 'Deal: ' + result;
  } catch {
    return userPlan;
  }
}

export function useSupportChat(): UseSupportChatReturn {
  const { pathname } = useLocation();
  const { setIncomingMessage, setOpenSupportChat, isSupportChatOpen } = useSupportChatStore();

  const token = Cookies.get(COOKIE_KEY.accessToken);
  const { isSignUpPage } = useRouteChecker({
    pathname,
  });
  const currentWorkspaceId = Cookies.get(COOKIE_KEY.workspaceSecret);
  const { data: listSubscription } = useListSubscriptionQuery({
    fetchPolicy: 'cache-first',
    skip: !token || isSignUpPage,
  });
  const { data: wallet } = useGetWalletDetailsV2Query({ skip: !token || isSignUpPage });

  const dollarAmount = wallet?.getWalletDetailsV2?.data?.amount ? wallet?.getWalletDetailsV2?.data?.amount / 100 : 0;

  const plan = listSubscription?.listSubscription?.edges
    ?.find((plan) => plan?.itemProductFamily === 'calilio-workspace')
    ?.subscriptionItems?.[0]?.itemPriceId?.split('calilio-workspace-')?.[1];

  const { data } = useauthMeQuery({
    skip: !token,
  });
  const profile = data?.authMe?.data?.user;

  const isAuthenticated = profile?.id;
  const { i18n } = useTranslation();
  const isDemo = pathname.startsWith('/demo');
  const themeMode = useEffectiveThemeMode();

  useEffect(() => {
    // Never mount the Crisp widget/script in the demo — there's no real
    // support session behind it and it would float over the sales demo.
    if (isDemo) return;

    Crisp.configure('e6f4a1e7-86c6-42a1-a6a8-fab86eba7059', {
      locale: isPublicRoute(pathname) ? 'en' : i18n.language,
    });
    if (isAuthenticated) {
      if (!isSupportChatOpen) {
        Crisp.chat.hide();
      }
      Crisp.chat.onChatClosed(() => {
        Crisp.chat.hide();
      });
    }
  }, [isAuthenticated, i18n, isDemo]);

  // Crisp renders in its own iframe and can't read our CSS tokens — pass the mode through its config API.
  useEffect(() => {
    if (isDemo || !window.$crisp) return;
    window.$crisp.push(['config', 'color:mode', [themeMode]]);
  }, [themeMode, isDemo]);

  // set custom data
  useEffect(() => {
    if (isDemo) return;
    if (isAuthenticated && plan) {
      const userName = profile?.name || 'N/A';
      const userEmail = profile?.email || 'guest';
      const userPhone = profile?.phoneNumber || 'N/A';
      const wksSecret = currentWorkspaceId || 'N/A';
      const userPlan = plan || 'N/A';
      const userWallet = dollarAmount || '0';

      // this prevents the visitor case for logged in user
      if (userEmail !== 'guest') {
        Crisp.user.setEmail(userEmail);
        Crisp.user.setNickname(userName);
        if (userPhone && userPhone !== 'N/A') {
          Crisp.user.setPhone(userPhone.replaceAll('+', ''));
        }
      }

      Crisp.session.setSegments([transformUserPlan(userPlan as UserPlan)], true);
      Crisp.session.setData({
        origin: 'Calilio Application',
        status: 'Logged In',
        name: userName,
        email: userEmail,
        phone: userPhone,
        plan: userPlan,
        workspaceSecret: wksSecret,
        wm: userWallet,
      });
    } else {
      Crisp.session.setSegments(['leads'], true);
      Crisp.session.setData({
        origin: 'Calilio Application',
        status: 'Not Logged In',
      });
    }

    Crisp.message.onMessageReceived(() => {
      const count = Crisp.chat.unreadCount() ?? 0;

      if (Crisp.chat.isChatOpened()) {
        return;
      }

      setIncomingMessage(count);
    });
    Crisp.chat.onChatOpened(() => {
      setIncomingMessage(0);
    });
  }, [isAuthenticated, dollarAmount, plan, isDemo]);

  function toggleSupportChat() {
    if (Crisp.chat.isChatOpened()) {
      setOpenSupportChat(false);
      Crisp.chat.close();
    } else {
      setOpenSupportChat(true);
      Crisp.chat.open();
    }
  }
  function openSupportChat() {
    Crisp.chat.open();
    setOpenSupportChat(true);
    if (isAuthenticated) Crisp.chat.show();
  }
  function closeSupportChat() {
    Crisp.chat.close();
    setOpenSupportChat(false);
    Crisp.setAvailabilityTooltip(false);
    if (isAuthenticated) Crisp.chat.hide();
  }
  function updateUser({ name, email }: SupportedUser) {
    Crisp.user.setEmail(email);
    Crisp.user.setNickname(name);
  }
  function trackEvent(name: string, data?: object) {
    Crisp.session.pushEvent(name, data);
  }
  function setMessage(message: string) {
    Crisp.message.setMessageText(message);
  }

  return {
    toggleSupportChat,
    openSupportChat,
    closeSupportChat,
    updateUserInChat: updateUser,
    trackEvent,
    setMessage,
  };
}
